import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import React from "react";
import { FormattedMessage } from "react-intl";
import { ICompanyPlanUsageFieldsFragment } from "~/lib/queries/fragments/__generated__/CompanyPlanUsageFields.generated";
import { IStripePlanFieldsFragment } from "~/lib/queries/fragments/__generated__/StripePlanFields.generated";

interface IShouldMeetAllRequirementsProps {
  toPlan: IStripePlanFieldsFragment | "BASIC";
  planUsage: ICompanyPlanUsageFieldsFragment;
  onCancel: () => void;
}

export default function ShouldMeetAllRequirements({ toPlan, planUsage, onCancel }: IShouldMeetAllRequirementsProps) {
  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 20,
        padding: 20,
        backgroundColor: palette.white,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 4,
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 4,
        }}
      >
        <h2
          css={{
            margin: 0,
          }}
        >
          <FormattedMessage defaultMessage="Required Changes" id="Z2n6mZ" />
        </h2>

        <p
          css={{
            margin: 0,
            color: palette.mediumGray,
            fontSize: 14,
            lineHeight: 1.33,
          }}
        >
          <FormattedMessage
            defaultMessage="Before you can downgrade your account to {plan}, you must first do the
          following:" id="Glb/wv"
            values={{
              plan: toPlan === "BASIC" ? "Basic" : toPlan.displayName,
            }}
          />
        </p>
      </div>

      {toPlan === "BASIC" ? (
        <ul
          css={{
            color: palette.red,
          }}
        >
          {planUsage.enrichedProducts > 0 && (
            <li>
              <FormattedMessage defaultMessage="Convert all enhanced product profiles to basic status" id="ERg5jF" />
            </li>
          )}

          {planUsage.productsWithMultipleTags > 0 && (
            <li>
              <FormattedMessage defaultMessage="Keep only 1 tag on products" id="hcZ213" />
            </li>
          )}

          {planUsage.teamMembers > 1 && (
            <li>
              <FormattedMessage defaultMessage="Remove all of your teammates from the account" id="RpXXzN" />
            </li>
          )}

          {planUsage.groups > 0 && (
            <li>
              <FormattedMessage defaultMessage="Close all existing sponsored groups" id="KQFAGP" />
            </li>
          )}

          {planUsage.groupInvitations > 0 && (
            <li>
              <FormattedMessage defaultMessage="Leave all other groups that you have personally joined" id="Td6PEj" />
            </li>
          )}

          {planUsage.sharingPage && (
            <li>
              <FormattedMessage defaultMessage="You must disable your sharing page" id="WDivKN" />
            </li>
          )}
        </ul>
      ) : (
        <ul
          css={{
            color: palette.red,
          }}
        >
          {planUsage.enrichedProducts - toPlan.limits.enrichedProducts > 0 && (
            <li>
              <FormattedMessage
                defaultMessage="Convert {count} enhanced product {count, plural, one {profile} other {profiles}} to Basic status" id="eiR9Bf"
                values={{
                  count: planUsage.enrichedProducts - toPlan.limits.enrichedProducts,
                }}
              />
            </li>
          )}

          {planUsage.teamMembers - toPlan.limits.teamMembers > 0 && (
            <li>
              <FormattedMessage
                defaultMessage="Remove {count} teammates from the account" id="kxjWmY"
                values={{
                  count: planUsage.teamMembers - toPlan.limits.teamMembers,
                }}
              />
            </li>
          )}

          {planUsage.groups - toPlan.limits.groups > 0 && (
            <li>
              <FormattedMessage
                defaultMessage="Close {count} of your sponsored groups" id="74g4yZ"
                values={{ count: planUsage.groups - toPlan.limits.groups }}
              />
            </li>
          )}

          {planUsage.groupInvitations - toPlan.limits.groupInvitations > 0 && (
            <li>
              <FormattedMessage
                defaultMessage="All account users must reduce their number of outside group memberships to {count}" id="zrr5Yz"
                values={{
                  count: planUsage.groupInvitations - toPlan.limits.groupInvitations,
                }}
              />
            </li>
          )}

          {planUsage.sharingPage && !toPlan.limits.sharingPage && (
            <li>
              <FormattedMessage defaultMessage="You must disable your sharing page" id="WDivKN" />
            </li>
          )}
        </ul>
      )}

      <Button color={palette.darkGray} theme="secondary" onClick={onCancel}>
        <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
      </Button>
    </div>
  );
}
